import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart } from 'lucide-react';
import Button from './Button';
import { formatDurationLabel, formatPrice, resolvePriceForValidity, sortDurationKeys } from '../../utils/helpers';

const ValiditySelectionModal = ({ 
  isOpen, 
  onClose, 
  product, 
  onAddToCart,
  title = "Select Billing Period"
}) => {
  const [selectedValidity, setSelectedValidity] = useState(null);

  // Get available pricing options
  const priceMap = useMemo(() => product?.pricingRaw || product?.Price || {}, [product]);
  
  // Get validity options with non-zero prices
  const validityOptions = useMemo(() => {
    if (!priceMap || typeof priceMap !== 'object') return [];
    return sortDurationKeys(Object.keys(priceMap))
      .map((key) => ({ validity: key, price: resolvePriceForValidity(priceMap, key) }))
      .filter(({ price }) => typeof price === 'number' && price > 0);
  }, [priceMap]);

  // Auto-select first option when modal opens
  React.useEffect(() => {
    if (isOpen && validityOptions.length > 0 && !selectedValidity) {
      setSelectedValidity(validityOptions[0].validity);
    }
  }, [isOpen, validityOptions, selectedValidity]);

  // Reset selection when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setSelectedValidity(null);
    }
  }, [isOpen]);

  const handleAddToCart = () => {
    if (!selectedValidity || !product) return;
    
    const selectedPrice = resolvePriceForValidity(priceMap, selectedValidity);
    onAddToCart(selectedValidity, selectedPrice);
    onClose();
  };

  const getDisplayPrice = () => {
    if (!selectedValidity) return 0;
    return resolvePriceForValidity(priceMap, selectedValidity) || 0;
  };

  if (!product || validityOptions.length === 0) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative mx-4 w-full max-w-md overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-2xl dark:border-gray-800 dark:bg-gray-900"
          >
            <div className="border-b border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 px-5 py-4 text-gray-950 dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-primary-700 dark:text-primary-100">
                    Billing period
                  </p>
                  <h3 className="mt-1 text-lg font-black">
                    {title}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="flex h-9 w-9 items-center justify-center rounded-2xl border border-primary-100 bg-white/80 text-gray-600 transition-colors hover:bg-white dark:border-white/10 dark:bg-white/[0.08] dark:text-gray-200 dark:hover:bg-white/[0.14]"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-5">
            {/* Header */}
            {/* Product Info */}
            <div className="mb-5 flex items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
              <img
                src={product.image || '/icon.png'}
                alt={product.name}
                className="h-12 w-12 rounded-2xl object-cover"
              />
              <div className="flex-1 min-w-0">
                <h4 className="truncate font-bold text-gray-950 dark:text-white">
                  {product.name}
                </h4>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Select your preferred billing period
                </p>
              </div>
            </div>

            {/* Validity Options */}
            <div className="mb-5 space-y-2">
              {validityOptions.map(({ validity, price }) => (
                <button
                  key={validity}
                  onClick={() => setSelectedValidity(validity)}
                  className={`w-full rounded-2xl border p-3 text-left transition-all duration-200 ${
                    selectedValidity === validity
                      ? 'border-primary-300 bg-primary-50 ring-4 ring-primary-500/10 dark:border-primary-500/40 dark:bg-primary-500/10'
                      : 'border-gray-100 bg-white hover:border-primary-200 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-500/40'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-black capitalize text-gray-950 dark:text-white">
                        {formatDurationLabel(validity)}
                      </div>
                      <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        Billed {formatDurationLabel(validity)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">
                        {formatPrice(price)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        /{formatDurationLabel(validity)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Selected Price Display */}
            {selectedValidity && (
              <div className="mb-5 rounded-2xl border border-primary-200 bg-primary-50 p-3 dark:border-primary-500/30 dark:bg-primary-500/10">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-primary-700 dark:text-primary-300">
                    Total Price
                  </span>
                  <span className="text-xl font-black text-primary-600 dark:text-primary-300 notranslate" translate="no">
                    {formatPrice(getDisplayPrice())}
                  </span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddToCart}
                disabled={!selectedValidity}
                icon={ShoppingCart}
                className="flex-1"
              >
                Add to Cart
              </Button>
            </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ValiditySelectionModal;
